import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Hand, QrCode } from 'lucide-react'
import { EmptyState } from '@/components/app/empty-state'
import { ListSkeleton } from '@/components/app/list-skeleton'
import { Screen } from '@/components/app/screen'
import { ScreenHeader } from '@/components/app/screen-header'
import { apiGetPaginated } from '@/lib/api'
import { SERVICE_TYPE_LABELS } from '@/lib/constants'
import { formatDateFR } from '@/lib/format'

export const Route = createFileRoute('/_fidele/mes-presences/')({
  component: MyAttendancesScreen,
})

interface Attendance {
  id: string
  method: 'QR' | 'MANUAL'
  createdAt: string
  service: { id: string; date: string; type: string; theme: string | null }
}

function MyAttendancesScreen() {
  const { data, isPending, isError } = useQuery({
    queryKey: ['member', 'attendances'],
    queryFn: () => apiGetPaginated<Attendance>('members/me/attendances', { limit: 50 }),
  })

  const total = data?.meta.total ?? 0

  return (
    <Screen withTabBar>
      <ScreenHeader
        title="Mes présences"
        subtitle={data ? `${total} culte${total > 1 ? 's' : ''}` : undefined}
      />

      <div className="px-5">
        {isPending && <ListSkeleton rows={5} />}

        {isError && (
          <EmptyState message="Impossible de charger vos présences. Vérifiez votre connexion." />
        )}

        {data && data.data.length === 0 && <EmptyState message="Aucune présence enregistrée." />}

        {data && data.data.length > 0 && (
          <ul className="flex flex-col gap-2.5">
            {data.data.map((item) => (
              <li
                key={item.id}
                className="flex items-center gap-3 rounded-[14px] border border-navy-800 bg-navy-900 px-4 py-3.5"
              >
                <span className="size-2 shrink-0 rounded-full bg-gold" aria-hidden="true" />

                <div className="min-w-0 flex-1">
                  <p className="truncate text-[15px] font-semibold text-chalk">
                    {SERVICE_TYPE_LABELS[item.service.type] ?? item.service.type}
                  </p>
                  {item.service.theme && (
                    <p className="truncate text-[13px] text-gold">{item.service.theme}</p>
                  )}
                  <p className="mt-0.5 text-[12px] text-steel">
                    {formatDateFR(item.service.date, {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </p>
                </div>

                {item.method === 'QR' ? (
                  <QrCode size={18} color="#C9A84C" aria-label="Présence scannée" />
                ) : (
                  <Hand size={18} color="#8A9BB0" aria-label="Présence saisie manuellement" />
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Screen>
  )
}
